/**
 * The Work task panel's policy: the Detail model built from a board row and the single-task read, the read's
 * request, the header pills and which ⋯ actions a person may take. Pure, so the panel's rules are tested without
 * mounting it; the bulk menu reads the same approval-ownership rule.
 */
import { JobConstructionAuthorisationKind, ServiceHistoryCompletionStatus, ServicePlanStatusSystemName, TaskApprovalStatus, WorkTaskScheduleRow, WorkTaskScheduleTask, WorkTaskScheduleTaskRequest, WorkTaskScheduleTaskVisit, heldByPerson, isFullyApproved } from '@jms/model';
import { DateTime } from 'luxon';
import { defineConditionalTag } from '../../shared/define.conditional.tag';
import { visitTaskTrailActions } from '../../visit/presentation/visit.task.trail';
import { VisitTaskTransition } from '../../visit/visit.movement.types';
import { WorkTaskDetailModel } from '../work.task.detail.model';

/** Which ⋯ actions the panel offers. All false on a read-only mount or without Manage. */
export interface WorkTaskDetailActions {
  approve: boolean;
  removeApproval: boolean;
  reschedule: boolean;
  undoMove: boolean;
  reset: boolean;
}

/** Whether an approval owns this task, so Remove approval can withdraw it (the bulk menu and the panel). */
export function ownsWorkTaskApproval(row: Pick<WorkTaskScheduleRow, 'approvalStatus'>): boolean {

  return heldByPerson(row.approvalStatus);
}

/** A forecast id is a one-way hash, so the row's plan and dates travel with it; undefined when the row lacks them. */
export function workTaskTaskRequest(row: WorkTaskScheduleRow): WorkTaskScheduleTaskRequest | undefined {

  if (!row?._id || !row.taskId || !row.servicePlan?._id || !row.date) return undefined;
  return {
    _id: row._id,
    taskId: row.taskId,
    servicePlanId: row.servicePlan._id,
    date: row.date,
    ...(row.rescheduledDate ? { rescheduledDate: row.rescheduledDate } : {}),
  };
}

/**
 * The panel's model. The board row alone (before the read answers, or when it fails) shows the row's own facts;
 * the read's row then replaces it, since it carries current approval and schedule state.
 */
export function workTaskDetailModel(boardRow: WorkTaskScheduleRow, task?: WorkTaskScheduleTask, history: VisitTaskTransition[] = []): WorkTaskDetailModel {

  const row = task?.row ?? boardRow;
  const visit = task?.visit;
  const approval = task?.approval;
  const manual = approval?.kind === JobConstructionAuthorisationKind.Manual;

  return {
    ...row,
    visitWhen: visit ? visitWhen(visit) : null,
    visitWindow: visit ? visitWindow(visit) : null,
    crew: (visit?.jobSummary?.crew ?? visit?.resources ?? []).filter(resource => !!resource?._id),
    contractor: visit?.contractor?._id ? visit.contractor : null,
    job: [row.job, visit?.job].find(job => !!job?._id) ?? null,
    frequencyLabel: [row.frequency, row.frequencyDescription].filter(Boolean).join(' · ') || null,
    description: task?.description || null,
    subtaskNames: task?.subtasks.map(subtask => subtask.name).filter(Boolean).join(', ') || null,
    approvedBy: manual ? approval.by ?? null : null,
    approvedAt: manual ? approval.at ?? null : null,
    changedSinceApproval: row.approvalStatus === TaskApprovalStatus.NeedsReview || approval?.state === TaskApprovalStatus.NeedsReview,
    openVisitId: visit ? visit.visitId ?? visit._id : row.visitId ?? null,
    timezone: visit?.timezone ?? null,
    stored: task?.stored === true,
    revision: task?.revision ?? null,
    history,
    approvalHistory: task?.approvalHistory ?? [],
  };
}

/**
 * The ⋯ actions for one task, the bulk menu's rules: resolved work and work of a plan that is not Started cannot
 * change; Approve, Remove approval, Undo and Reset apply only before a Job exists (a Job's tasks change through
 * its own workflow). Undo and Reset need a stored row whose chain allows them, as the Visit history panel does.
 * Reschedule is still refused by the server for a Job in progress or inside the change cutoff.
 */
export function workTaskDetailActions(model: WorkTaskDetailModel, canManage: boolean): WorkTaskDetailActions {

  const editable = canManage
    && model.completionStatus !== ServiceHistoryCompletionStatus.Resolved
    && (!model.planStatus || model.planStatus === ServicePlanStatusSystemName.Started);
  const planned = editable && model.scheduleState === 'Planned';
  const owned = ownsWorkTaskApproval(model);
  const trail = visitTaskTrailActions(model.history);

  return {
    approve: planned && !isFullyApproved(model.approvalStatus),
    removeApproval: planned && owned,
    reschedule: editable,
    undoMove: planned && model.stored && !!trail.undoes,
    reset: planned && model.stored && trail.canReset,
  };
}

/** Header pill naming the Job the task is in; it takes the approval pill's place, which hides once a Job exists. */
export const workTaskJobTag = defineConditionalTag<WorkTaskDetailModel>(model => model?.job?._id ? { label: model.job.name || model.job._id, tone: 'brand' } : null);

/** Header pill naming the task's frequency band. */
export const workTaskFrequencyTag = defineConditionalTag<WorkTaskDetailModel>(model => model?.frequency ? { label: model.frequency, tone: 'neutral' } : null);

/** "Fri 30 Oct 2026 · 08:00–12:00", or "· Any time" for an all-day Visit, in the Visit's zone; the panel and the portal dialog. */
export function visitWhen(visit: WorkTaskScheduleTaskVisit): string {

  const start = DateTime.fromJSDate(new Date(visit.startDate), { zone: visit.timezone });
  const end = DateTime.fromJSDate(new Date(visit.endDate), { zone: visit.timezone });
  const day = start.isValid ? start.toFormat('ccc d LLL yyyy') : visit.localDate;
  if (visit.allDay || !start.isValid || !end.isValid) return `${day} · Any time`;

  return `${day} · ${start.toFormat('HH:mm')}–${end.toFormat('HH:mm')}`;
}

/** The days the work counts on when they span more than the Visit's own day; null otherwise. */
function visitWindow(visit: WorkTaskScheduleTaskVisit): string | null {

  if (visit.complianceWindowLabel) return visit.complianceWindowLabel;
  if (!visit.windowStart || !visit.windowEnd || visit.windowStart === visit.windowEnd) return null;

  return `Can be done ${localDay(visit.windowStart)} – ${localDay(visit.windowEnd)}`;
}

function localDay(date: string): string {

  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toFormat('d LLL') : date;
}
